import * as TYPES from './questionnaires-actiontypes';
import { createQuestionAnswer } from './question-answer-creator';
import { validateQuestionnaire } from './questionnaire-validator';
import { persistStateToSession, getStateFromSession } from '../../webstorage';

const LOCAL_STORAGE_KEY = 'questionnaires:selectedQuestionnaireId';
const LOCAL_STORAGE_KEY_QUESTIONNAIRES_ORIGIN = 'questionnaires:questionnairesOrigin';
const LOCAL_STORAGE_KEY_QUESTIONNAIRE_SCORING_PAGE = 'questionnaires:questionnaireScoringPage';
const LOCAL_STORAGE_KEY_COMPLETE_QUESTIONNAIRE_PAGE = 'questionnaires:completeQuestionnairePage';

export const initialState = {
  assignedQuestionnaires: null,
  historicalQuestionnaires: [],
  newQuestionnaireCount: 0,
  selectedQuestionnaireId: getStateFromSession(LOCAL_STORAGE_KEY, ''),
  patientsWithActiveQuestionnaires: [],
  filteredPatients: [],
  questionnairesOrigin: getStateFromSession(
    LOCAL_STORAGE_KEY_QUESTIONNAIRES_ORIGIN,
    '/questionnaires'
  ),
  questionnaireScoringPage: getStateFromSession(
    LOCAL_STORAGE_KEY_QUESTIONNAIRE_SCORING_PAGE,
    '/questionnaire-scoring'
  ),
  completeQuestionnairePage: getStateFromSession(
    LOCAL_STORAGE_KEY_COMPLETE_QUESTIONNAIRE_PAGE,
    '/complete-questionnaire'
  ),
};

const processMultiAnswerQuestion = (oldAnswers, newAnswer) => {
  let isInList = false;
  const newAnswers = [];
  oldAnswers.forEach((answer) => {
    if (answer.listAnswerId === newAnswer.listAnswerId) {
      isInList = true;
      if (newAnswer.isChecked) {
        newAnswers.push(newAnswer);
      }
    } else {
      newAnswers.push(answer);
    }
    return true;
  });

  if (!isInList && newAnswer.isChecked) {
    newAnswers.push(newAnswer);
  }

  return newAnswers;
};

const updateAnswers = (questionnaire, action) => {
  let answers = questionnaire.patientQuestionAnswerList;

  const questionnaireItem = questionnaire.questionnaireItems
    .find((item) => item.questionnaireItemId === action.questionnaireItemId);

  const newAnswer = createQuestionAnswer(questionnaireItem, action.answerValue);

  if (questionnaireItem.questionnaireItemType === 'MultiSelectionQuestion') {
    answers = processMultiAnswerQuestion(answers, newAnswer);
  } else if (answers.find((a) => a.questionnaireItemId === action.questionnaireItemId) === undefined) {
    answers.push(newAnswer);
  } else {
    answers = questionnaire.patientQuestionAnswerList.map((a) => {
      if (a.questionnaireItemId === action.questionnaireItemId) {
        return { ...a, ...newAnswer };
      }
      return a;
    });
  }

  return { ...questionnaire, patientQuestionAnswerList: answers };
};

const setDetailsForQuestionnaireInQuestionnaires = (questionnaire, questionnaires) => {
  if (questionnaires.find(
    (q) => q.assignedQuestionnaireId === questionnaire.assignedQuestionnaireId) === undefined) {
    questionnaires.push(questionnaire);
    return questionnaires;
  }
  return questionnaires.map((q) => {
    if (q.assignedQuestionnaireId === questionnaire.assignedQuestionnaireId) {
      return { ...q, ...questionnaire };
    }
    return q;
  });
};

export const questionnairesReducer = (state = initialState, action) => {
  let newState;
  switch (action.type) {
    case TYPES.QUESTIONNAIRES_LOADED:
      newState = { ...state, assignedQuestionnaires: action.questionnaires };
      break;
    case TYPES.NEW_QUESTIONNAIRES_COUNT_LOADED:
      newState = { ...state, newQuestionnaireCount: action.count };
      break;
    case TYPES.QUESTIONNARE_DETAILS_LOADED:
      newState = { ...state,
        assignedQuestionnaires: setDetailsForQuestionnaireInQuestionnaires(
          action.questionnaire, state.assignedQuestionnaires.slice(0)) };
      persistStateToSession(LOCAL_STORAGE_KEY, state.selectedQuestionnaireId);
      break;
    case TYPES.QUESTIONNAIRE_SUBMITTED:
      newState = { ...state,
        assignedQuestionnaires: state.assignedQuestionnaires.map((q) => {
          if (q.assignedQuestionnaireId === action.questionnaire.assignedQuestionnaireId) {
            return { ...q, ...action.questionnaire };
          }
          return q;
        }) };
      break;
    case TYPES.QUESTIONNAIRE_SELECTED:
      newState = { ...state, selectedQuestionnaireId: action.id };
      break;
    case TYPES.QUESTIONNAIRE_ANSWER_UPDATED:
      newState = { ...state,
        assignedQuestionnaires: state.assignedQuestionnaires.map((q) => {
          if (q.assignedQuestionnaireId === action.questionnaireId) {
            return updateAnswers(q, action);
          }
          return q;
        }) };
      break;
    case TYPES.QUESTIONNAIRE_VALIDATED:
      newState = { ...state,
        assignedQuestionnaires: state.assignedQuestionnaires.map((q) => {
          if (q.assignedQuestionnaireId === action.questionnaireId) {
            return validateQuestionnaire(q);
          }
          return q;
        }) };
      break;
    case TYPES.QUESTIONNAIRE_ANSWERSCLEARED:
      newState = { ...state,
        assignedQuestionnaires: state.assignedQuestionnaires.map((q) => {
          if (q.assignedQuestionnaireId === action.questionnaireId) {
            return { ...q, patientQuestionAnswerList: [] };
          }
          return q;
        }) };
      break;
    case TYPES.QUESTIONNAIRE_SET_FILTER:
      newState = { ...state, filter: action.value };
      break;
    case TYPES.HISTORICAL_QUESTIONNAIRES_LOADED:
      newState = { ...state, historicalQuestionnaires: action.historicalQuestionnaires };
      break;
    case TYPES.HISTORICAL_QUESTIONNARE_DETAILS_LOADED:
      newState = { ...state,
        historicalQuestionnaires: state.historicalQuestionnaires.map((q) => {
          if (q.assignedQuestionnaireId === action.historicalQuestionnaire.assignedQuestionnaireId) {
            return { ...q, ...action.historicalQuestionnaire };
          }
          return q;
        }) };
      break;
    case TYPES.KIOSK_QUESTIONNAIRES_LOADED:
      newState = { ...state,
        patientsWithActiveQuestionnaires: action.kioskQuestionnaires,
        filteredPatients: action.kioskQuestionnaires };
      break;
    case TYPES.KIOSK_QUESTIONNAIRES_FILTERED:
      newState = { ...state,
        filteredPatients: action.state.facilityName === 'all' ?
          state.patientsWithActiveQuestionnaires :
          state.patientsWithActiveQuestionnaires.filter(
            (patient) => patient.facilityShortName === action.state.facilityName
          ) };
      break;
    case TYPES.QUESTIONNAIRES_ORIGIN_UPDATED:
      newState = { ...state, ...action.state };
      persistStateToSession(LOCAL_STORAGE_KEY_QUESTIONNAIRES_ORIGIN, action.state.questionnairesOrigin);
      persistStateToSession(LOCAL_STORAGE_KEY_QUESTIONNAIRE_SCORING_PAGE, action.state.questionnaireScoringPage);
      persistStateToSession(
        LOCAL_STORAGE_KEY_COMPLETE_QUESTIONNAIRE_PAGE,
        action.state.completeQuestionnairePage
      );
      break;
    default:
      newState = state;
  }

  return newState;
};

export default questionnairesReducer;
